import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Anchor, Shield, Scale, Award, Users, FileText, AlertCircle, Clock, DollarSign, Heart, ChevronRight, BookOpen, ArrowRight, Calculator } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import PracticeAreaFAQ from '@/components/PracticeAreaFAQ';
import PageVideoSection from '@/components/PageVideoSection';

// Maritime-specific videos
const maritimeVideos = [
  {
    id: 'wkM7_oOG900',
    title: "From Ship to Shore: Maritime Expertise",
    description: "My journey from working on vessels to becoming a maritime injury advocate.",
    duration: "1:52"
  },
  {
    id: 'QC_d7NBlvFI',
    title: "The Jones Act: Maintenance and Cure",
    description: "What maintenance and cure means for injured seamen and how it impacts you.",
    duration: "3:08"
  },
  {
    id: 'NfKXHOpQBX0',
    title: "Why Choose Me as Your Maritime Lawyer",
    description: "My unique background representing both employers and injured workers.",
    duration: "1:04"
  },
  {
    id: 'YE2L1nOosDM',
    title: "Maximum Medical Improvement (MMI)",
    description: "When maintenance and cure benefits stop and what it means for your recovery.",
    duration: "1:02"
  }
];

const maritimeFAQs = [
  {
    question: "What is the Jones Act and how does it protect maritime workers?",
    answer: "The Jones Act (46 U.S.C. § 30104) is a federal law that allows seamen injured due to employer negligence to sue for damages. Unlike regular workers' compensation, it lets you recover full compensation including pain and suffering, lost wages, and future earning capacity. You only need to prove your employer was slightly negligent."
  },
  {
    question: "What is 'maintenance and cure' and am I entitled to it?",
    answer: "Maintenance and cure is a maritime benefit that covers your daily living expenses (maintenance) and medical care (cure) if you're injured while working on a vessel. Unlike the Jones Act, you're entitled to these benefits regardless of fault—your employer must pay them until you reach maximum medical improvement."
  },
  {
    question: "How long do I have to file a Jones Act claim in Washington?",
    answer: "You have 3 years from the date of your injury to file a Jones Act claim. However, I strongly recommend contacting an attorney immediately—evidence like vessel logs and witness statements can disappear quickly, and early action protects your rights."
  },
  {
    question: "Do I qualify as a 'seaman' under maritime law?",
    answer: "To qualify as a seaman, you must spend at least 30% of your work time on a vessel in navigation and have a substantial connection to that vessel's operation. This includes deckhands, fishermen, tugboat crew, offshore rig workers, and many other maritime occupations. I can evaluate your specific situation."
  },
  {
    question: "Can I sue my employer if I was partially at fault for my injury?",
    answer: "Yes. Under the Jones Act, you can recover damages even if you were partially at fault. Your compensation will be reduced by your percentage of fault, but you can still recover. This is more favorable than many state laws that might bar recovery entirely."
  },
  {
    question: "What's the difference between the Jones Act and workers' compensation?",
    answer: "The Jones Act typically provides much greater compensation than workers' comp. You can recover full damages for pain and suffering, which workers' comp doesn't cover. You can also sue for employer negligence and unseaworthiness, and there's no cap on damages."
  }
];

const MaritimePage = () => {
  const highRiskOccupations = [
    'Fishermen', 'Deckhands', 'Longshoremen', 'Seamen', 'Shipyard workers',
    'Harbor pilots', 'Tugboat crew', 'Offshore platform workers', 'Commercial divers',
    'Cruise ship crew', 'Maritime construction workers', 'Ferry operators',
    'Maritime engineers', 'Port workers', 'Marine cargo handlers'
  ];

  // HowTo schema for "Filing a Jones Act Claim"
  const howToSteps = {
    name: "How to File a Jones Act Claim in Washington or Alaska",
    description: "Step-by-step guide for injured maritime workers to file a Jones Act negligence claim and secure maintenance and cure benefits.",
    totalTime: "PT60M",
    steps: [
      { name: "Report Your Injury Immediately", text: "Notify your supervisor or captain of your injury as soon as it happens. Request written documentation of the incident report." },
      { name: "Seek Medical Attention", text: "Get medical care right away. Your employer must provide cure (medical treatment) regardless of fault. Document all injuries and treatments." },
      { name: "Document Everything", text: "Take photos of the accident scene, unsafe conditions, and your injuries. Collect names of witnesses. Keep copies of all medical records." },
      { name: "Request Maintenance and Cure", text: "Formally request maintenance (daily living expenses) and cure (medical care) from your employer in writing. These benefits are owed regardless of fault." },
      { name: "Do Not Sign Employer Documents", text: "Do not sign any settlement offers, releases, or statements from your employer or their insurance without legal review." },
      { name: "Contact a Maritime Attorney", text: "Speak with an experienced Jones Act attorney before the 3-year statute of limitations expires. An attorney can evaluate negligence and unseaworthiness claims." },
      { name: "File Your Jones Act Claim", text: "Your attorney will file the negligence claim, gather evidence of unsafe conditions, and negotiate with the employer's insurance for maximum compensation." }
    ]
  };

  return (
    <div className="min-h-screen pt-20" data-testid="maritime-page">
      <SEOHead 
        title="Maritime Injury & Jones Act Attorney | Washington & Alaska | Amy C. Brown"
        description="Experienced maritime injury lawyer handling Jones Act claims, maintenance and cure, and unseaworthiness cases for seamen, deckhands, and maritime workers in Washington and Alaska."
        serviceData={{
          name: "Maritime Injury & Jones Act Legal Representation",
          description: "Legal representation for maritime workers injured on vessels, including Jones Act negligence claims, maintenance and cure benefits, and unseaworthiness actions.",
          category: "Maritime Law"
        }}
        howToData={howToSteps}
      />
      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <span className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              Alaska and Washington
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
              Maritime Injury
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Maritime work offers rewarding opportunities offshore and a dependable livelihood, yet it carries the inherent risk of unforeseen accidents and injuries. In Alaska and Washington State, where the maritime industry flourishes, injuries sustained in these professions require the expertise of a legal advocate well-versed in the complexities of maritime law. As a dedicated maritime injury lawyer, I am prepared to assist you. Contact me today to begin addressing your case with the attention and skill it deserves.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all duration-300"
                data-testid="maritime-hero-call"
              >
                <Phone className="w-5 h-5" />
                (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Trust Me */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Why Trust Me?
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                If you've been injured while working on a ship or at sea, you need a lawyer who knows maritime law and will fight hard for you. I graduated with honors from Seattle University School of Law, where I learned how to build strong legal arguments to win cases. I worked as a law extern in a federal court in Western Washington, seeing firsthand how judges make decisions. I also worked at a law firm that represented big national clients, so I understand how insurance companies try to shortchange workers like you. I'm ready to stand up to them on your behalf.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Anchor className="w-10 h-10 text-ocean-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Maritime Law Expertise</h4>
                <p className="text-sm text-slate-600">I specialize in cases like yours, including Jones Act claims, to get you the maintenance, cure, and compensation you're owed after an injury.</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Shield className="w-10 h-10 text-ocean-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Defense Experience</h4>
                <p className="text-sm text-slate-600">With experience at a maritime law firm, I know how maritime employers and insurers try to limit payouts, and I'll use that knowledge to fight for every dollar you deserve.</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Heart className="w-10 h-10 text-ocean-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Maintenance and Cure Knowledge</h4>
                <p className="text-sm text-slate-600">I understand your right to maintenance (living expenses) and cure (medical care) under maritime law, ensuring you get the full benefits you're entitled to while you recover.</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Users className="w-10 h-10 text-ocean-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Direct Support</h4>
                <p className="text-sm text-slate-600">You'll work directly with me, not a paralegal, so I can focus on your specific situation and keep you in the loop every step of the way.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Jones Act Expertise */}
      <section className="py-20 bg-ocean-soft">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              Jones Act Expertise
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              I specialize in Jones Act cases, helping injured seamen like you secure maintenance, cure, and fair compensation for injuries caused by employer negligence or unseaworthy vessels.
            </p>
          </div>
        </div>
      </section>

      {/* Contingency Fee */}
      <section className="py-20 bg-ocean-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-white/20 px-4 py-2 rounded-full text-sm font-medium mb-6">
                You Don't Pay Me Unless You Win
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Contingency Fee Basis
              </h2>
              <p className="text-lg text-ocean-100 leading-relaxed mb-6">
                I operate on a contingency fee basis, meaning you do not pay upfront legal fees. My compensation is contingent upon securing a successful outcome for your case—only when you win do I receive a percentage of the settlement or award. This approach alleviates financial pressure during an already challenging time, allowing you to focus on recovery rather than costs. With over ten years of experience, I am committed to maximizing your recovery, ensuring my interests align with yours.
              </p>
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center gap-2 bg-white text-ocean-700 px-8 py-4 rounded-full font-semibold hover:bg-ocean-50 transition-all duration-300"
                data-testid="maritime-contingency-call"
              >
                <Phone className="w-5 h-5" />
                Free Consultation: (425) 675-1224
              </a>
              <Link 
                to="/pricing"
                className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all duration-300"
              >
                <DollarSign className="w-5 h-5" />
                View Full Pricing
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <DollarSign className="w-10 h-10 text-ocean-300 mx-auto mb-4" />
                <h4 className="font-semibold mb-2">Maintenance</h4>
                <p className="text-sm text-ocean-200">Did you know that if you get hurt while working on board a vessel and cannot work, you have right to a stipend that pays for your food and lodging?</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <Heart className="w-10 h-10 text-ocean-300 mx-auto mb-4" />
                <h4 className="font-semibold mb-2">Cure</h4>
                <p className="text-sm text-ocean-200">Did you know that if you become sick while working on board a vessel and cannot work, you must receive a stipend that pays for your food and lodging?</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* I Can Protect Your Rights */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
              I Can Protect Your Rights
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Navigating its complexities requires more than general legal knowledge; it demands precision, experience, and a deep understanding of the unique rules that govern the seas. If your lawyer doesn't know the difference between Jones Act claims and general negligence—or how maritime jurisdiction can make or break your case.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              I've spent years immersed in maritime law. I know the tides, the traps, and the tactics that get results. When your livelihood, your job, or your injury claim is on the line, you need someone who knows how to fight in this arena—and win. <strong>I don't just know what the law says you deserve. I know how to make sure you get it.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Jones Act vs Regular Civil Law */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              How the Jones Act Differs from Regular Civil Law
            </h2>
            <p className="text-lg text-slate-600 max-w-4xl mx-auto">
              If you're a maritime worker injured on the job, you may wonder why your case falls under the Jones Act instead of standard civil law, like most personal injury cases. The <a href="https://www.law.cornell.edu/uscode/text/46/subtitle-III/chapter-301" target="_blank" rel="noopener noreferrer" className="text-ocean-600 underline hover:text-ocean-800">Jones Act (Merchant Marine Act of 1920)</a> is a federal law designed specifically to protect seamen—workers like deckhands, fishermen, or offshore rig workers—and it differs significantly from regular civil law in ways that directly affect your rights and recovery.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover-lift">
              <div className="w-14 h-14 bg-ocean-100 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-ocean-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Who It Covers
              </h3>
              <p className="text-slate-600 leading-relaxed">
                The Jones Act applies only to "seamen," meaning workers who spend a significant portion of their time working on a vessel in navigation, such as ships, barges, or rigs. Regular civil law covers land-based workers, like those injured in car accidents or workplace incidents, without requiring this specific maritime connection. As a maritime lawyer, I can determine your seaman status to ensure you benefit from the Jones Act's protections.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover-lift">
              <div className="w-14 h-14 bg-ocean-100 rounded-xl flex items-center justify-center mb-6">
                <Scale className="w-7 h-7 text-ocean-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Proving Negligence
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Under the Jones Act, you only need to show that your employer's negligence contributed to your injury, even slightly, to recover compensation. This is a lower burden of proof than in regular civil law, where you often must prove the defendant was primarily at fault. Additionally, the Jones Act allows claims for "unseaworthiness," holding shipowners accountable for unsafe vessel conditions, which has no equivalent in standard civil law.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover-lift">
              <div className="w-14 h-14 bg-ocean-100 rounded-xl flex items-center justify-center mb-6">
                <Award className="w-7 h-7 text-ocean-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Types of Benefits
              </h3>
              <p className="text-slate-600 leading-relaxed">
                The Jones Act provides unique benefits like maintenance (daily living expenses) and cure (medical care) to support injured seamen until they reach maximum medical improvement. Regular civil law doesn't offer these specific maritime benefits, typically focusing on lump-sum damages for pain, suffering, or lost wages. My experience ensures you receive the full scope of Jones Act benefits, tailored to your maritime injury.
              </p>
            </div>
          </div>

          <div className="mt-12 bg-ocean-600 rounded-2xl p-8 text-white text-center">
            <p className="text-lg mb-4">
              My specialized knowledge of the Jones Act allows me to navigate its unique protections to secure the compensation and support you're entitled to as a maritime worker. Unlike general personal injury lawyers, I understand these distinctions and will fight to maximize your recovery under maritime law.
            </p>
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center gap-2 bg-white text-ocean-700 px-8 py-3 rounded-full font-semibold hover:bg-ocean-50 transition-all"
            >
              <Phone className="w-5 h-5" />
              Call me at 425-675-1224
            </a>
          </div>
        </div>
      </section>

      {/* My Defense Experience */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                My Experience Defending National Maritime Companies Works for You
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                When choosing a lawyer for your maritime injury case, you want someone who knows the industry inside and out. I previously worked at a defense law firm representing the insurance companies of large national maritime employers, giving me a unique edge in fighting for you. This experience means I understand the strategies these companies and their insurers use to minimize payouts or deny claims, whether it's questioning your status as a seaman under the Jones Act or downplaying the unseaworthiness of a vessel. I've seen their playbook firsthand and know how to counter it effectively.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                At Amy C. Brown Law, I use this insider knowledge to build a strong case for you, whether you're seeking maintenance, cure, or compensation for your injuries. My background defending maritime companies allows me to anticipate their moves and fight for the full benefits you're entitled to under maritime law. Trust me to turn my experience into your advantage, ensuring you get the justice and support you deserve.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_492a0bf8-a316-49a1-8dbd-117f79877488/artifacts/ou7kss9o_IMG_2176-2-e1756077431100.jpg"
                alt="Amy C. Brown - Jones Act and maritime injury attorney with experience representing injured seamen in Washington and Alaska"
                title="Maritime injury lawyer Amy C. Brown"
                loading="lazy"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Maritime Workers Need Specialized Lawyer */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary text-center mb-12">
            Why Maritime Workers Need a Lawyer with Maritime Law Experience
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <FileText className="w-10 h-10 text-ocean-600 mb-6" />
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Navigating Complex Maritime Laws
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Maritime law, including the Jones Act, general maritime law, and the Longshore and Harbor Workers' Compensation Act, has specific requirements and protections not found in standard personal injury law. A lawyer with maritime experience understands how to apply these laws to secure benefits like maintenance (living expenses), cure (medical care), and damages for employer negligence or unseaworthy vessels, ensuring you don't miss out on what you're entitled to.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <AlertCircle className="w-10 h-10 text-ocean-600 mb-6" />
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Investigating Maritime-Specific Issues
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Maritime injuries often involve unique issues like unseaworthiness or unsafe working conditions on ships, rigs, or docks. A lawyer experienced in maritime law knows how to investigate these claims, gather evidence like vessel logs or safety records, and hold shipowners accountable, unlike a general injury lawyer who may lack the expertise to address these maritime-specific factors.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <Shield className="w-10 h-10 text-ocean-600 mb-6" />
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Countering Maritime Insurance Tactics
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Insurance companies for maritime employers are skilled at minimizing payouts, often using tactics unique to the industry, such as disputing your status as a "seaman" under the Jones Act. A lawyer with maritime law experience, especially one who has worked with these insurance companies, knows their strategies and can fight effectively to protect your rights and maximize your compensation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* High Risk Occupations */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
              Helping Injured Maritime Workers in Washington and Alaska
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              The nation, in many ways, is dependent on the maritime industry. Without competent and well-trained maritime workers, much of the nation's function as a whole would come to a halt. For this reason, I believe that injured maritime workers deserve to be adequately compensated and provided with the resources they need to move on with their lives. If you've been hurt while holding a naval occupation, please don't hesitate to speak with me. I am a knowledgeable Everett personal injury lawyer at Amy C Brown Law with extensive offshore legal experience. I work in Alaska and Washington State and I handle a wide range of maritime injury cases.
            </p>

            <div className="bg-ocean-soft rounded-2xl p-8">
              <h3 className="font-heading text-xl font-semibold text-secondary mb-6">
                High Risk Maritime Operations
              </h3>
              <p className="text-slate-600 mb-6">
                Within the maritime industry, certain occupations are more prone to serious injury than others. Most of the maritime injury cases I see stem from the following occupations:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {highRiskOccupations.map((occupation, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-ocean-500 rounded-full"></span>
                    <span className="text-slate-700 text-sm">{occupation}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Contact Now */}
      <section className="py-20 bg-secondary text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">
            Why You Should Contact Me Now for Your Maritime Injury
          </h2>
          <p className="text-lg text-slate-300 text-center max-w-4xl mx-auto mb-12">
            If you've been injured while working on a ship, rig, or dock, reaching out to a maritime lawyer as soon as possible is critical to protecting your rights and securing the compensation you deserve. Delaying can jeopardize your case and limit your options.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <Clock className="w-10 h-10 text-ocean-400 mb-6" />
              <h3 className="font-heading text-xl font-semibold mb-4">
                Preserving Critical Evidence
              </h3>
              <p className="text-slate-300 leading-relaxed">
                Maritime injury cases, such as those under the Jones Act or general maritime law, often rely on evidence like vessel logs, safety reports, or witness statements. These can be lost, altered, or harder to obtain over time. Contacting me now allows me to start investigating immediately, ensuring key evidence is preserved to strengthen your claim.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <AlertCircle className="w-10 h-10 text-ocean-400 mb-6" />
              <h3 className="font-heading text-xl font-semibold mb-4">
                Meeting Strict Deadlines
              </h3>
              <p className="text-slate-300 leading-relaxed">
                Maritime law has specific deadlines, such as statutes of limitations for filing <a href="https://www.law.cornell.edu/uscode/text/46/30106" target="_blank" rel="noopener noreferrer" className="text-ocean-300 underline hover:text-ocean-100">Jones Act claims (46 U.S.C. § 30106)</a> or <a href="https://www.dol.gov/agencies/owcp/dlhwc" target="_blank" rel="noopener noreferrer" className="text-ocean-300 underline hover:text-ocean-100">Longshore and Harbor Workers' Compensation Act</a> claims, which can be as short as one to three years depending on the case. Waiting too long risks missing these deadlines, potentially barring you from recovering maintenance, cure, or other compensation. I can ensure your claim is filed on time.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <DollarSign className="w-10 h-10 text-ocean-400 mb-6" />
              <h3 className="font-heading text-xl font-semibold mb-4">
                Securing Benefits Sooner
              </h3>
              <p className="text-slate-300 leading-relaxed">
                As an injured maritime worker, you're entitled to benefits like maintenance (living expenses) and cure (medical care) to support your recovery. Delaying contact with a lawyer experienced in maritime law could delay these payments, adding financial stress. By reaching out now, I can work quickly to secure the benefits you need to focus on healing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <PracticeAreaFAQ
        title="Maritime Injury FAQs"
        subtitle="Common questions about Jones Act claims, maintenance and cure, and maritime worker rights."
        faqs={maritimeFAQs}
        accentColor="ocean"
        schemaId="maritime"
      />

      {/* Video Resources Section */}
      <PageVideoSection
        videos={maritimeVideos}
        title="Maritime Injury Videos"
        subtitle="Watch these videos to learn about your rights as a maritime worker and the Jones Act."
        theme="teal"
      />

      {/* Helpful Resources - Internal Linking */}
      <section className="py-16 bg-slate-50" data-testid="maritime-resources-section">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-ocean-100 text-ocean-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <BookOpen className="w-4 h-4" />
              Learn More
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-3">
              Resources for Maritime Workers
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Understanding the Jones Act and your maritime rights is crucial to getting fair compensation.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Link
              to="/blog/understanding-jones-act-maritime-workers"
              className="group bg-white hover:bg-ocean-50 rounded-xl p-6 border border-slate-200 hover:border-ocean-300 transition-all duration-300 shadow-sm"
            >
              <Anchor className="w-10 h-10 text-ocean-600 mb-4" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-ocean-700 transition-colors">
                Understanding the Jones Act
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Learn how the Jones Act protects maritime workers and what compensation you may be entitled to.
              </p>
              <span className="inline-flex items-center gap-1 text-ocean-600 font-medium text-sm group-hover:gap-2 transition-all">
                Read Guide <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link
              to="/blog/maintenance-and-cure-explained"
              className="group bg-white hover:bg-ocean-50 rounded-xl p-6 border border-slate-200 hover:border-ocean-300 transition-all duration-300 shadow-sm"
            >
              <Heart className="w-10 h-10 text-ocean-600 mb-4" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-ocean-700 transition-colors">
                Maintenance & Cure Benefits
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Your employer owes you living expenses and medical care regardless of fault. Here's what you need to know.
              </p>
              <span className="inline-flex items-center gap-1 text-ocean-600 font-medium text-sm group-hover:gap-2 transition-all">
                Learn More <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link
              to="/compensation-estimator"
              className="group bg-white hover:bg-ocean-50 rounded-xl p-6 border border-slate-200 hover:border-ocean-300 transition-all duration-300 shadow-sm"
            >
              <Calculator className="w-10 h-10 text-ocean-600 mb-4" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-ocean-700 transition-colors">
                What Is My Case Worth?
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Use our free calculator to get an estimate of your potential compensation.
              </p>
              <span className="inline-flex items-center gap-1 text-ocean-600 font-medium text-sm group-hover:gap-2 transition-all">
                Try Calculator <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-ocean-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Learn More About Me
          </h2>
          <p className="text-xl text-ocean-100 leading-relaxed mb-8">
            I understand the unique challenges maritime workers face after an injury. Contacting me now means I can start fighting for your rights immediately, using my maritime law expertise to build a strong case and counter insurance company tactics. Don't wait—reach out today to protect your future and get the justice you deserve.
          </p>
          <a 
            href="tel:425-675-1224"
            className="inline-flex items-center gap-3 bg-white text-ocean-700 px-10 py-5 rounded-full font-bold text-xl hover:bg-ocean-50 transition-all duration-300 shadow-xl"
            data-testid="maritime-final-cta"
          >
            <Phone className="w-6 h-6" />
            Call me at (425) 675-1224
          </a>
          <p className="mt-6 text-ocean-200 text-sm">
            Page last updated: December 2025
          </p>
        </div>
      </section>
    </div>
  );
};

export default MaritimePage;
